import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Typography,
  Avatar,
  Dropdown,
  Space,
  Tag,
  message,
  Modal,
  Descriptions,
  Input,
  InputNumber,
  Divider,
  Row,
  Col,
  Progress
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export default function EvaluationReview() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [facilitatorNotes, setFacilitatorNotes] = useState('');
  const [overrideScore, setOverrideScore] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingEvaluations();
  }, []);

  const fetchPendingEvaluations = async () => {
    try {
      setLoading(true);
      // Fetch all lab sessions that are evaluated but not released
      const response = await api.get('/api/facilitator/evaluations/pending');
      setEvaluations(response.data || []);
    } catch (error) {
      message.error('Failed to load evaluations');
      console.error('Error fetching evaluations:', error);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (sessionId) => {
    try {
      // Get evaluation details for this session
      const response = await api.get(`/api/lab-sessions/${sessionId}/evaluation`);
      setSelectedEvaluation(response.data);
      setFacilitatorNotes(response.data.facilitator_notes || '');
      setOverrideScore(null);
      setReviewModalVisible(true);
    } catch (error) {
      message.error('Failed to load evaluation details');
      console.error('Error fetching evaluation:', error);
    }
  };

  const handleSaveReview = async () => {
    if (!selectedEvaluation) return;

    Modal.confirm({
      title: 'Save Review?',
      content: 'This will save your notes but not release the feedback to the student yet.',
      onOk: async () => {
        try {
          setSubmitting(true);
          // For now, just update local state since we don't have a separate save endpoint
          message.success('Review notes saved');
        } catch (error) {
          message.error('Failed to save review');
          console.error('Error saving review:', error);
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleRelease = async () => {
    if (!selectedEvaluation) return;

    Modal.confirm({
      title: 'Release Feedback to Student?',
      content: 'This will make the evaluation results visible to the student.',
      onOk: async () => {
        try {
          setSubmitting(true);
          await api.post(`/api/lab-sessions/${selectedEvaluation.lab_session_id}/evaluation/release-feedback`, {
            facilitator_notes: facilitatorNotes
          });
          message.success('Evaluation released to student');
          setReviewModalVisible(false);
          setSelectedEvaluation(null);
          setFacilitatorNotes('');
          setOverrideScore(null);
          fetchPendingEvaluations();
        } catch (error) {
          message.error('Failed to release evaluation');
          console.error('Error releasing evaluation:', error);
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/facilitator/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const evaluationColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{`${record.user.first_name} ${record.user.last_name}`}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Lab',
      dataIndex: ['lab', 'name'],
      key: 'lab',
    },
    {
      title: 'Training',
      dataIndex: ['training', 'name'],
      key: 'training',
    },
    {
      title: 'Score',
      key: 'score',
      render: (_, record) => (
        <Space>
          <Progress
            type="circle"
            percent={Math.round(record.overall_score)}
            width={50}
            strokeColor={
              record.overall_score >= 80 ? '#52c41a' :
              record.overall_score >= 60 ? '#faad14' :
              '#ff4d4f'
            }
          />
          <Text strong>{Math.round(record.overall_score)}%</Text>
        </Space>
      ),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'completed' ? 'orange' : 'green'}>
          {status === 'completed' ? 'PENDING REVIEW' : status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleReview(record.id)}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/facilitator/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Evaluation Review</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Evaluation Review</Title>
          <Text type="secondary">Review AI-generated evaluations and release to students</Text>
        </div>

        <Card>
          <Table
            columns={evaluationColumns}
            dataSource={evaluations}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} evaluations`,
            }}
          />
        </Card>
      </Content>

      {/* Review Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: '#faad14' }} />
            <Text strong>Evaluation Review</Text>
          </Space>
        }
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          setSelectedEvaluation(null);
          setFacilitatorNotes('');
          setOverrideScore(null);
        }}
        width={900}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setReviewModalVisible(false);
              setSelectedEvaluation(null);
              setFacilitatorNotes('');
              setOverrideScore(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="default"
            onClick={handleSaveReview}
            loading={submitting}
          >
            Save Review
          </Button>,
          <Button
            key="release"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleRelease}
            loading={submitting}
          >
            Release to Student
          </Button>,
        ]}
      >
        {selectedEvaluation && selectedEvaluation.status === 'evaluated' && (
          <>
            {/* Evaluation Info */}
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Lab Session ID" span={2}>
                <Text code>{selectedEvaluation.lab_session_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Completed">
                {new Date(selectedEvaluation.evaluation_completed_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedEvaluation.feedback_released_to_student ? 'green' : 'orange'}>
                  {selectedEvaluation.feedback_released_to_student ? 'RELEASED' : 'PENDING REVIEW'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Passed" span={2}>
                <Tag color={selectedEvaluation.passed ? 'success' : 'error'}>
                  {selectedEvaluation.passed ? 'PASSED' : 'FAILED'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* AI Summary */}
            <Card
              title="AI Evaluation Summary"
              size="small"
              style={{ marginBottom: 24, backgroundColor: '#fafafa' }}
            >
              <Text>{selectedEvaluation.ai_summary}</Text>
            </Card>

            {/* Overall Score */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <Card size="small">
                  <Space direction="vertical" align="center" style={{ width: '100%' }}>
                    <Text type="secondary">Overall Score</Text>
                    <Progress
                      type="circle"
                      percent={Math.round(selectedEvaluation.overall_score)}
                      strokeColor={
                        selectedEvaluation.overall_score >= 80 ? '#52c41a' :
                        selectedEvaluation.overall_score >= 60 ? '#faad14' :
                        '#ff4d4f'
                      }
                      size={120}
                    />
                    <Text strong style={{ fontSize: 18 }}>{selectedEvaluation.overall_score.toFixed(1)}%</Text>
                    <Text type="secondary">Tokens Used: {selectedEvaluation.total_tokens_used}</Text>
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Criteria Scores */}
            <Card
              title={
                <Space>
                  <StarOutlined style={{ color: '#faad14' }} />
                  <Text strong>Individual Criteria Results</Text>
                </Space>
              }
              size="small"
              style={{ marginBottom: 24 }}
            >
              {selectedEvaluation.criterion_results && selectedEvaluation.criterion_results.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {selectedEvaluation.criterion_results.map((criterion, index) => (
                    <div key={criterion.id}>
                      <Row justify="space-between" align="middle">
                        <Col span={16}>
                          <Space direction="vertical" size={0}>
                            <Text strong>Criterion {index + 1}</Text>
                            <Tag color={criterion.passed ? 'success' : 'error'} style={{ marginTop: 4 }}>
                              {criterion.passed ? 'PASSED' : 'FAILED'}
                            </Tag>
                          </Space>
                        </Col>
                        <Col span={8} style={{ textAlign: 'right' }}>
                          <Space>
                            <Progress
                              percent={Math.round(criterion.score * 100)}
                              steps={10}
                              strokeColor={criterion.passed ? '#52c41a' : '#ff4d4f'}
                              style={{ width: 120 }}
                            />
                            <Text strong>{(criterion.score * 100).toFixed(0)}%</Text>
                          </Space>
                        </Col>
                      </Row>
                      <div style={{ marginTop: 8 }}>
                        <Text strong>AI Feedback:</Text>
                        <div style={{
                          marginTop: 4,
                          padding: 8,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 4,
                          fontSize: 12
                        }}>
                          <Text>{criterion.ai_feedback}</Text>
                        </div>
                      </div>
                      {index < selectedEvaluation.criterion_results.length - 1 && <Divider style={{ margin: '16px 0' }} />}
                    </div>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No criteria results available</Text>
              )}
            </Card>

            {/* Facilitator Notes */}
            <Card title="Facilitator Notes" size="small">
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Add your own notes and feedback before releasing to the student:
              </Text>
              <TextArea
                rows={4}
                value={facilitatorNotes}
                onChange={(e) => setFacilitatorNotes(e.target.value)}
                placeholder="Optional: Add additional feedback, guidance, or corrections for the student..."
              />
              {selectedEvaluation.facilitator_reviewed && selectedEvaluation.facilitator_notes && (
                <div style={{ marginTop: 8, padding: 8, backgroundColor: '#f0f2f5', borderRadius: 4 }}>
                  <Text type="secondary" strong>Previous Notes:</Text>
                  <Text style={{ display: 'block', marginTop: 4 }}>{selectedEvaluation.facilitator_notes}</Text>
                </div>
              )}
            </Card>
          </>
        )}
      </Modal>
    </Layout>
  );
}
